import { useCallback, useEffect, useState } from 'react';

import { Check, Close, ContentCopy, Delete, Edit, MoreVert, Refresh } from '@mui/icons-material';
import {
	Box,
	Chip,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Select,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';

import { useToastService } from '@@common';

import { ContextScopeModel, DeepOperationModel, OperationExecutionTypeModel, OperationExecutionTypesModel, OperationExposureModel } from '../../../api/api';
import McpIcon from '../../../common/components/McpIcon';
import { useKeyDown } from '../../../common/hooks/useKeyDown';
import { getOperationMethodChipColor, getOperationMethodLabel } from '../Utils/operationDisplay';
import { OperationExposureChannel, channelsToExposure, exposureToChannels, participatesInApi, participatesInMcp } from '../Utils/operationExposure';
import OperationScopeChips from './OperationScopeChips';

interface OperationHeaderProps {
	operation: DeepOperationModel;
	executionTypes: OperationExecutionTypeModel[];
	availableScopes: ContextScopeModel[];
	onScopesUpdated: () => void;
	onRefresh?: (operationId: string) => void | Promise<void>;
	onUpdateOperation?: (operationId: string, name: string, executionType: OperationExecutionTypesModel) => Promise<void>;
	onUpdateExposure?: (operationId: string, exposure: OperationExposureModel) => Promise<void>;
	onUpdateDescription?: (operationId: string, description: string) => Promise<void>;
	onDelete?: () => void;
}

const EDIT_CANCEL_KEYS = ['Escape'];

export default function OperationHeader({
	operation,
	executionTypes,
	availableScopes,
	onScopesUpdated,
	onRefresh,
	onUpdateOperation,
	onUpdateExposure,
	onUpdateDescription,
	onDelete,
}: OperationHeaderProps) {
	const toastService = useToastService();
	const [isEditingOperation, setIsEditingOperation] = useState(false);
	const [operationNameDraft, setOperationNameDraft] = useState(operation.name);
	const [executionTypeDraft, setExecutionTypeDraft] = useState(operation.executionType);
	const [isOperationSaving, setIsOperationSaving] = useState(false);
	const [isExposureSaving, setIsExposureSaving] = useState(false);
	const [isEditingDescription, setIsEditingDescription] = useState(false);
	const [descriptionDraft, setDescriptionDraft] = useState(operation.description);
	const [isDescriptionSaving, setIsDescriptionSaving] = useState(false);
	const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
	const isActionsMenuOpen = Boolean(actionsAnchorEl);

	useEffect(() => {
		setOperationNameDraft(operation.name);
		setExecutionTypeDraft(operation.executionType);
		setIsEditingOperation(false);
		setDescriptionDraft(operation.description);
		setIsEditingDescription(false);
	}, [operation.id, operation.name, operation.executionType, operation.description]);

	const handleStartEditOperation = () => {
		setOperationNameDraft(operation.name);
		setExecutionTypeDraft(operation.executionType);
		setIsEditingOperation(true);
	};

	const handleCancelEditOperation = useCallback(() => {
		setOperationNameDraft(operation.name);
		setExecutionTypeDraft(operation.executionType);
		setIsEditingOperation(false);
	}, [operation.name, operation.executionType]);

	const handleOperationEditorKeyDown = useCallback(
		(event: KeyboardEvent) => {
			event.preventDefault();
			handleCancelEditOperation();
		},
		[handleCancelEditOperation],
	);

	useKeyDown(EDIT_CANCEL_KEYS, handleOperationEditorKeyDown, isEditingOperation && !isOperationSaving);

	const handleSaveOperation = async () => {
		const nextName = operationNameDraft.trim();
		if (!onUpdateOperation || !nextName) {
			return;
		}

		if (nextName === operation.name && executionTypeDraft === operation.executionType) {
			setIsEditingOperation(false);
			return;
		}

		setIsOperationSaving(true);
		try {
			await onUpdateOperation(operation.id, nextName, executionTypeDraft);
			setIsEditingOperation(false);
		} finally {
			setIsOperationSaving(false);
		}
	};

	const handleExposureChange = async (exposure: OperationExposureModel) => {
		if (!onUpdateExposure || exposure === operation.exposure) {
			return;
		}

		setIsExposureSaving(true);
		try {
			await onUpdateExposure(operation.id, exposure);
		} finally {
			setIsExposureSaving(false);
		}
	};

	const handleStartEditDescription = () => {
		setDescriptionDraft(operation.description);
		setIsEditingDescription(true);
	};

	const handleCancelEditDescription = useCallback(() => {
		setDescriptionDraft(operation.description);
		setIsEditingDescription(false);
	}, [operation.description]);

	const handleDescriptionEditorKeyDown = useCallback(
		(event: KeyboardEvent) => {
			event.preventDefault();
			handleCancelEditDescription();
		},
		[handleCancelEditDescription],
	);

	useKeyDown(EDIT_CANCEL_KEYS, handleDescriptionEditorKeyDown, isEditingDescription && !isDescriptionSaving);

	const handleSaveDescription = async () => {
		const nextDescription = descriptionDraft.trim();
		if (!onUpdateDescription || !nextDescription || nextDescription === operation.description) {
			setIsEditingDescription(false);
			return;
		}

		setIsDescriptionSaving(true);
		try {
			await onUpdateDescription(operation.id, nextDescription);
			setIsEditingDescription(false);
		} finally {
			setIsDescriptionSaving(false);
		}
	};

	const handleCloseActionsMenu = () => {
		setActionsAnchorEl(null);
	};

	const handleEditOperation = () => {
		handleCloseActionsMenu();
		handleStartEditOperation();
	};

	const handleCopyOperationId = () => {
		navigator.clipboard.writeText(operation.id);
		toastService.success('Copied to clipboard');
		handleCloseActionsMenu();
	};

	const handleDeleteOperation = () => {
		handleCloseActionsMenu();
		onDelete?.();
	};

	return (
		<>
			<Stack direction="row" sx={{ mb: 1, alignItems: 'flex-start', justifyContent: 'space-between' }}>
				<Box sx={{ flex: 1, pt: 1 }}>
					{isEditingOperation ? (
						<Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: 560 }}>
							<TextField
								size="small"
								value={operationNameDraft}
								disabled={isOperationSaving}
								onChange={(event) => setOperationNameDraft(event.target.value)}
								sx={{ flex: 1 }}
							/>
							<Select
								size="small"
								value={executionTypeDraft}
								disabled={isOperationSaving}
								onChange={(event) => setExecutionTypeDraft(event.target.value as OperationExecutionTypesModel)}
								sx={{ minWidth: 140 }}
							>
								{executionTypes.map((type) => (
									<MenuItem key={type.id} value={type.id}>
										{getOperationMethodLabel(type.id)}
									</MenuItem>
								))}
							</Select>
							<Tooltip title="Save operation">
								<span>
									<IconButton size="small" color="primary" disabled={isOperationSaving || !operationNameDraft.trim()} onClick={handleSaveOperation}>
										<Check fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
							<Tooltip title="Cancel">
								<span>
									<IconButton size="small" disabled={isOperationSaving} onClick={handleCancelEditOperation}>
										<Close fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</Stack>
					) : (
						<>
							<Typography
								component="span"
								variant="h6"
								onClick={handleStartEditOperation}
								sx={{ verticalAlign: 'middle', display: 'inline', mr: 1, cursor: 'pointer', fontSize: '1.35rem', fontWeight: 600, lineHeight: 1.3 }}
							>
								{operation.name}
							</Typography>
							<Tooltip title="Edit operation">
								<IconButton
									size="small"
									onClick={handleStartEditOperation}
									sx={{
										mr: 1.25,
										verticalAlign: 'middle',
										color: 'text.disabled',
										'&:hover': {
											color: 'text.secondary',
										},
									}}
								>
									<Edit fontSize="small" />
								</IconButton>
							</Tooltip>
							{participatesInApi(operation.exposure) && (
								<Chip
									label={getOperationMethodLabel(operation.executionType)}
									size="small"
									color={getOperationMethodChipColor(operation.executionType)}
									onClick={handleStartEditOperation}
									sx={{ mr: 1, verticalAlign: 'middle', cursor: 'pointer' }}
								/>
							)}
							{participatesInMcp(operation.exposure) && (
								<Tooltip title="Exposed through MCP">
									<Chip
										icon={<McpIcon size={16} />}
										label="MCP"
										size="small"
										sx={{
											mr: 1,
											verticalAlign: 'middle',
											bgcolor: 'primary.main',
											color: 'primary.contrastText',
											'& .MuiChip-icon': {
												color: 'primary.contrastText',
												mr: -0.75,
											},
											'& .MuiChip-label': {
												pl: 0.25,
											},
										}}
									/>
								</Tooltip>
							)}
						</>
					)}
				</Box>
				<Stack direction="row" spacing={4} alignItems="center">
					<OperationScopeChips
						operationId={operation.id}
						operationName={operation.name}
						assignedScopeIds={operation.scopes || []}
						availableScopes={availableScopes}
						onScopesUpdated={onScopesUpdated}
					/>
					<Stack direction="column" spacing={0.5} alignItems="flex-start">
						<Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', flexShrink: 0 }}>
							Exposure
						</Typography>
						<ToggleButtonGroup
							size="small"
							aria-label="Operation exposure"
							sx={{
								flex: '0 0 auto',
								'& .MuiToggleButton-root': {
									minHeight: 28,
									py: 0.25,
								},
								'& .MuiToggleButton-root.Mui-selected': {
									bgcolor: 'primary.main',
									borderColor: 'primary.main',
									color: 'primary.contrastText',
									'&:hover': {
										bgcolor: 'primary.dark',
									},
								},
							}}
							value={exposureToChannels(operation.exposure)}
							disabled={!onUpdateExposure || isExposureSaving}
							onChange={(_, channels: OperationExposureChannel[]) => {
								if (channels.length === 0) {
									return;
								}

								handleExposureChange(channelsToExposure(channels));
							}}
						>
							<ToggleButton value="api" sx={{ minWidth: 56 }}>
								API
							</ToggleButton>
							<ToggleButton value="mcp" sx={{ minWidth: 56 }}>
								MCP
							</ToggleButton>
						</ToggleButtonGroup>
					</Stack>
					<Stack direction="row" spacing={0.5}>
						{onRefresh && (
							<Tooltip title="Refresh operation">
								<IconButton onClick={() => onRefresh(operation.id)} size="small">
									<Refresh fontSize="small" />
								</IconButton>
							</Tooltip>
						)}
						<Tooltip title="More actions">
							<IconButton size="small" onClick={(event) => setActionsAnchorEl(event.currentTarget)}>
								<MoreVert fontSize="small" />
							</IconButton>
						</Tooltip>
						<Menu anchorEl={actionsAnchorEl} open={isActionsMenuOpen} onClose={handleCloseActionsMenu}>
							<MenuItem onClick={handleEditOperation}>
								<ListItemIcon>
									<Edit fontSize="small" />
								</ListItemIcon>
								<ListItemText>Edit operation</ListItemText>
							</MenuItem>
							<MenuItem onClick={handleCopyOperationId}>
								<ListItemIcon>
									<ContentCopy fontSize="small" />
								</ListItemIcon>
								<ListItemText>Copy operation ID</ListItemText>
							</MenuItem>
							{onDelete && (
								<MenuItem onClick={handleDeleteOperation}>
									<ListItemIcon>
										<Delete fontSize="small" color="error" />
									</ListItemIcon>
									<ListItemText>Delete operation</ListItemText>
								</MenuItem>
							)}
						</Menu>
					</Stack>
				</Stack>
			</Stack>
			<Box sx={{ mb: 1, width: '100%', minHeight: 64 }}>
				{isEditingDescription ? (
					<Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: '100%', minHeight: 64 }}>
						<TextField
							fullWidth
							size="small"
							multiline
							minRows={2}
							value={descriptionDraft}
							disabled={isDescriptionSaving}
							onChange={(event) => setDescriptionDraft(event.target.value)}
						/>
						<Tooltip title="Save description">
							<span>
								<IconButton size="small" color="primary" disabled={isDescriptionSaving || !descriptionDraft.trim()} onClick={handleSaveDescription}>
									<Check fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Cancel">
							<span>
								<IconButton size="small" disabled={isDescriptionSaving} onClick={handleCancelEditDescription}>
									<Close fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					</Stack>
				) : (
					<Stack direction="row" spacing={0.5} alignItems="center" sx={{ minHeight: 64 }}>
						<Typography variant="body2" color="text.secondary" sx={{ lineHeight: '22px' }}>
							{operation.description}
						</Typography>
						{onUpdateDescription && (
							<Tooltip title="Edit description">
								<IconButton
									onClick={handleStartEditDescription}
									sx={{
										width: 22,
										height: 22,
										p: 0.25,
										color: 'text.disabled',
										'&:hover': {
											color: 'text.secondary',
										},
									}}
								>
									<Edit sx={{ fontSize: 16 }} />
								</IconButton>
							</Tooltip>
						)}
					</Stack>
				)}
			</Box>
		</>
	);
}
